import { type FC, useCallback, useEffect, useState } from 'react';
import { graphql, createRefetchContainer, type RelayRefetchProp } from 'react-relay/legacy.js';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { trackFollowSearch, authLabels } from './followSearchTracking.ts';
import { getBuyerId } from 'dibs-cookie-jar';
import { addLoginHandlers } from 'dibs-buyer-layout/exports/loginHandlers.jsx';

import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader.js';
const { LOGIN, FOLLOW_DEALER_FLOW } = authModalLoader.constants;

import { type FollowButton_viewer$data } from './__generated__/FollowButton_viewer.graphql.ts';
import { commitFollowSearch } from './commitFollowSearch.ts';

type Props = {
    viewer: FollowButton_viewer$data;
    followSearchUriRef: string;
    followSearchDisplayName: string;
    relay: RelayRefetchProp;
    gaLabel: string;
    location: string;
};

const FollowButtonComponent: FC<Props> = props => {
    const { viewer, gaLabel, followSearchUriRef, followSearchDisplayName, relay, location } = props;
    const [isLoading, setIsLoading] = useState(false);
    const [didRefetchAfterLogin, setDidRefetchAfterLogin] = useState(false);
    const [followSearchId, setFollowSearchId] = useState<string | null | undefined>(null);

    const refetchFollowing = useCallback(
        (onCompleted: () => void = () => setIsLoading(false)) => {
            const userId = getBuyerId(document.cookie);
            if (userId) {
                setIsLoading(true);
                relay.refetch(
                    { userId, hasUserId: !!userId, followSearchUriRef },
                    null,
                    onCompleted
                );
            } else {
                setIsLoading(false);
            }
        },
        [followSearchUriRef, relay]
    );
    useEffect(() => {
        // setup refetch on initial load and login
        refetchFollowing();
        addLoginHandlers(() => refetchFollowing());
    }, [refetchFollowing]);
    useEffect(() => {
        setFollowSearchId(viewer.user?.favorites?.edges?.[0]?.node?.serviceId);
    }, [viewer.user]);

    const commitMutation = useCallback(() => {
        commitFollowSearch(relay.environment, {
            userId: getBuyerId(document.cookie),
            followSearchId,
            followSearchUriRef,
            followSearchDisplayName,
            onCompleted: resp => {
                trackFollowSearch({ label: gaLabel, isAdded: !followSearchId });
                setFollowSearchId(resp.favoritePage?.user?.favorites?.edges?.[0]?.node?.serviceId);
                setIsLoading(false);
            },
            onError: () => setIsLoading(false),
        });
    }, [followSearchDisplayName, followSearchId, followSearchUriRef, gaLabel, relay.environment]);
    useEffect(() => {
        // follow search after login/refetch
        if (didRefetchAfterLogin) {
            if (!followSearchId) {
                commitMutation();
            } else {
                setIsLoading(false); // already following
            }
            setDidRefetchAfterLogin(false);
        }
    }, [commitMutation, didRefetchAfterLogin, followSearchId]);

    async function toggleIsFollowing(): Promise<void> {
        const userId = getBuyerId(document.cookie);
        setIsLoading(true);
        if (userId) {
            // logged in case: toggle follow
            commitMutation();
        } else {
            // logged out case: handle auth, refetch, then trigger follow mutation
            await authModalLoader.show({
                flow: FOLLOW_DEALER_FLOW,
                action: LOGIN,
                ga: { label: authLabels[location] },
            });
            refetchFollowing(() => {
                setDidRefetchAfterLogin(true);
            });
        }
    }

    return (
        <Button
            disabled={isLoading}
            type="secondary"
            size="small"
            onClick={toggleIsFollowing}
            dataTn="item-actions-follow-search"
        >
            {followSearchId ? (
                <FormattedMessage id="FollowButton.following" defaultMessage="Following" />
            ) : (
                <FormattedMessage id="FollowButton.follow" defaultMessage="Follow" />
            )}
        </Button>
    );
};

export const FollowButton = createRefetchContainer(
    FollowButtonComponent,
    {
        viewer: graphql`
            fragment FollowButton_viewer on Viewer
            @argumentDefinitions(
                userId: { type: "String", defaultValue: "" }
                hasUserId: { type: "Boolean", defaultValue: false }
                followSearchUriRef: { type: "String", defaultValue: "" }
            ) {
                user(userId: $userId) @include(if: $hasUserId) {
                    favorites(first: 1, types: [SEARCH], pages: [$followSearchUriRef]) {
                        edges {
                            node {
                                serviceId
                            }
                        }
                    }
                }
            }
        `,
    },
    graphql`
        query FollowButtonRefetchQuery(
            $userId: String!
            $hasUserId: Boolean!
            $followSearchUriRef: String!
        ) {
            viewer {
                ...FollowButton_viewer
                    @arguments(
                        userId: $userId
                        hasUserId: $hasUserId
                        followSearchUriRef: $followSearchUriRef
                    )
            }
        }
    `
);
